/**
 * Messaging Integration Service
 * Handles integration between messaging and other features (tasks, contracts, checklists, forms)
 */

import { doc, updateDoc, getDoc, setDoc, serverTimestamp, addDoc, collection, arrayUnion } from 'firebase/firestore';
import { db } from '@/firebase/config';
import { messagingService } from '@/firebase/messagingService';
import { handleFirebaseAuthError } from '@/utils/firebaseErrorHandler';
import type { Message, Conversation } from '@/types/messaging';
import type { Task } from '@/services/taskService';

export interface CreateTaskFromMessageContext {
  messageId: string;
  conversationId: string;
  messageText: string;
  attachments?: Message['attachments'];
  participants: string[]; // Suggested assignees
  organizationId: string;
}

export interface MessagingIntegrationResult<T> {
  success: boolean;
  data?: T;
  error?: string;
}

/**
 * Link a task to a message after task creation
 */
export async function linkTaskToMessage(
  messageId: string,
  taskId: string
): Promise<MessagingIntegrationResult<void>> {
  try {
    const messageRef = doc(db, 'messages', messageId);
    const messageSnap = await getDoc(messageRef);

    if (!messageSnap.exists()) {
      return { success: false, error: 'Message not found' };
    }

    await updateDoc(messageRef, {
      'linkedItems.taskId': taskId,
      updatedAt: serverTimestamp(),
    });

    return { success: true };
  } catch (error: any) {
    console.error('Error linking task to message:', error);
    await handleFirebaseAuthError(error);
    return { success: false, error: error.message || 'Failed to link task to message' };
  }
}

/**
 * Link a task to a conversation
 */
export async function linkTaskToConversation(
  conversationId: string,
  taskId: string
): Promise<MessagingIntegrationResult<void>> {
  try {
    const conversationRef = doc(db, 'conversations', conversationId);
    const conversationSnap = await getDoc(conversationRef);

    if (!conversationSnap.exists()) {
      return { success: false, error: 'Conversation not found' };
    }

    const conversationData = conversationSnap.data();
    const originalType = conversationData?.type || 'direct';
    
    // Preserve 'group' type if it was originally a group conversation (user-created)
    // Preserve other contextual types ('contract', 'checklist', 'form') if already set
    // Only set to 'task' if it's 'direct' or doesn't have a specific type
    const newType = (originalType === 'group' || originalType === 'contract' || originalType === 'checklist' || originalType === 'form') 
      ? originalType 
      : 'task';

    await updateDoc(conversationRef, {
      'linkedItems.taskId': taskId,
      type: newType,
      updatedAt: serverTimestamp(),
    });

    return { success: true };
  } catch (error: any) {
    console.error('Error linking task to conversation:', error);
    await handleFirebaseAuthError(error);
    return { success: false, error: error.message || 'Failed to link task to conversation' };
  }
}

/**
 * Get conversations linked to a specific task
 */
export async function getConversationsForTask(
  taskId: string
): Promise<MessagingIntegrationResult<Conversation[]>> {
  try {
    // Query conversations where linkedItems.taskId == taskId
    const { collection, query, where, getDocs } = await import('firebase/firestore');
    const { db } = await import('@/firebase/config');
    
    const conversationsRef = collection(db, 'conversations');
    const conversationsQuery = query(conversationsRef, where('linkedItems.taskId', '==', taskId));
    const conversationsSnapshot = await getDocs(conversationsQuery);
    
    const conversations: Conversation[] = [];
    conversationsSnapshot.forEach((doc) => {
      const data = doc.data();
      // Verify this conversation actually has the linkedItems.taskId
      if (data.linkedItems?.taskId === taskId) {
        conversations.push({ id: doc.id, ...data } as Conversation);
      }
    });
    
    console.log(`Found ${conversations.length} conversation(s) linked to task ${taskId}`);
    return { success: true, data: conversations };
  } catch (error: any) {
    // If it's an index error, log it but don't fail - we'll fall back to messageContext check
    if (error.code === 'failed-precondition' || error.message?.includes('index')) {
      console.warn('Firestore index may be required for linkedItems.taskId query. Please create an index or wait for auto-indexing:', error);
      // Return empty array instead of failing - we'll fall back to messageContext check
      return { success: true, data: [] };
    }
    console.error('Error getting conversations for task:', error);
    await handleFirebaseAuthError(error);
    return { success: false, error: error.message || 'Failed to get conversations for task' };
  }
}

/**
 * Update task with message context after creation
 */
export async function updateTaskWithMessageContext(
  taskId: string,
  messageContext: {
    createdFromMessageId?: string;
    conversationId?: string;
    lastDiscussedAt?: any;
  }
): Promise<MessagingIntegrationResult<void>> {
  try {
    const taskRef = doc(db, 'tasks', taskId);
    const taskSnap = await getDoc(taskRef);

    if (!taskSnap.exists()) {
      return { success: false, error: 'Task not found' };
    }

    await updateDoc(taskRef, {
      messageContext: {
        createdFromMessageId: messageContext.createdFromMessageId,
        conversationId: messageContext.conversationId,
        lastDiscussedAt: messageContext.lastDiscussedAt || serverTimestamp(),
      },
      updatedAt: serverTimestamp(),
    });

    return { success: true };
  } catch (error: any) {
    console.error('Error updating task with message context:', error);
    await handleFirebaseAuthError(error);
    return { success: false, error: error.message || 'Failed to update task with message context' };
  }
}

/**
 * Get message context for a task (if task was created from a message)
 */
export async function getTaskMessageContext(
  taskId: string
): Promise<MessagingIntegrationResult<{ messageId?: string; conversationId?: string }>> {
  try {
    const taskRef = doc(db, 'tasks', taskId);
    const taskSnap = await getDoc(taskRef);

    if (!taskSnap.exists()) {
      return { success: false, error: 'Task not found' };
    }

    const taskData = taskSnap.data() as Task;
    const messageContext = taskData.messageContext || {};

    return {
      success: true,
      data: {
        messageId: messageContext.createdFromMessageId,
        conversationId: messageContext.conversationId,
      },
    };
  } catch (error: any) {
    console.error('Error getting task message context:', error);
    await handleFirebaseAuthError(error);
    return { success: false, error: error.message || 'Failed to get task message context' };
  }
}

/**
 * Sync participants for a task conversation
 * Removes users who no longer have access and adds users who should have access
 * This can be called when a conversation is loaded to ensure participants are up to date
 */
export async function syncTaskConversationParticipants(
  conversationId: string,
  taskId: string,
  taskLocationId: string,
  organizationId: string,
  userId: string,
  taskOwnerRole?: string
): Promise<MessagingIntegrationResult<void>> {
  try {
    // Get the conversation
    const conversationResult = await messagingService.getConversation(conversationId);
    if (!conversationResult.success || !conversationResult.data) {
      return { success: false, error: 'Conversation not found' };
    }

    const conversation = conversationResult.data;
    
    // CRITICAL: Never auto-sync regular groups - they are manually managed
    if (conversation.type === 'group') {
      console.log('[syncTaskConversationParticipants] Skipping sync - regular group conversations are manually managed');
      return { success: true }; // Return success but don't modify
    }
    
    // Verify this is a task conversation
    if (conversation.linkedItems?.taskId !== taskId) {
      return { success: false, error: 'Conversation is not linked to this task' };
    }

    // Get task data to check assignedUserIds
    const { doc: docRef, getDoc: getDocRef } = await import('firebase/firestore');
    const { db: dbRef } = await import('@/firebase/config');
    const taskRefForAssigned = docRef(dbRef, 'tasks', taskId);
    const taskSnapForAssigned = await getDocRef(taskRefForAssigned);
    const assignedUserIds = taskSnapForAssigned.exists() 
      ? (taskSnapForAssigned.data()?.assignedUserIds || (taskSnapForAssigned.data()?.assignedUserId ? [taskSnapForAssigned.data().assignedUserId] : []))
      : [];
    
    // Get all users who should have access
    const allAccessibleUsers = await getUsersWithTaskAccess(taskLocationId, organizationId, taskOwnerRole, assignedUserIds);
    const currentParticipants = conversation.participants || [];
    
    // Find users who should have access but aren't in the conversation
    const missingUsers = allAccessibleUsers.filter(userId => !currentParticipants.includes(userId));
    
    // Find users who are in the conversation but no longer have access (should be removed)
    // BUT: Check conversationParticipants collection to see if user was manually added
    // For manually added members (those in conversationParticipants but not in allAccessibleUsers),
    // we should be more careful about removing them - only remove if they're definitely not supposed to be there
    const { collection: collectionRef, query: queryRef, where: whereRef, getDocs: getDocsRef } = await import('firebase/firestore');
    
    // Check which participants are manually added (exist in conversationParticipants)
    const participantsCollectionRef = collectionRef(dbRef, 'conversationParticipants');
    const participantsQuery = queryRef(
      participantsCollectionRef,
      whereRef('conversationId', '==', conversationId)
    );
    const participantsSnapshot = await getDocsRef(participantsQuery);
    const manuallyAddedParticipants = new Set<string>();
    participantsSnapshot.forEach((doc) => {
      manuallyAddedParticipants.add(doc.data().userId);
    });
    
    const usersToRemove = currentParticipants.filter(userId => {
      // Don't remove if user is in allAccessibleUsers
      if (allAccessibleUsers.includes(userId)) {
        return false;
      }
      // For manually added members (those in conversationParticipants), be more conservative
      // Only remove if they're definitely not supposed to be there AND they're not admins/managers
      // For now, we'll be conservative and not auto-remove manually added members
      // They should be removed explicitly by an admin
      if (manuallyAddedParticipants.has(userId)) {
        console.log(`[syncTaskConversation] Preserving manually added member ${userId}`);
        return false;
      }
      return true;
    });
    
    const { doc, updateDoc, arrayUnion, arrayRemove, serverTimestamp, setDoc, deleteDoc } = await import('firebase/firestore');
    const { db } = await import('@/firebase/config');
    const conversationRef = doc(db, 'conversations', conversationId);
    
    // Remove users who no longer have access (but preserve manually added members)
    if (usersToRemove.length > 0) {
      for (const userIdToRemove of usersToRemove) {
        try {
          // Try using removeGroupMember first (requires admin)
          const removeResult = await messagingService.removeGroupMember(conversationId, userIdToRemove, userId);
          if (!removeResult.success) {
            // If that fails (user not admin), directly remove the participant for task conversations
            // This is safe because we've verified the user no longer has task access
            // AND they're not a manually added member (we filtered those out above)
            await updateDoc(conversationRef, {
              participants: arrayRemove(userIdToRemove),
              updatedAt: serverTimestamp(),
            });
            
            // Delete participant document directly
            const participantId = `${conversationId}_${userIdToRemove}`;
            const participantRef = doc(db, 'conversationParticipants', participantId);
            await deleteDoc(participantRef);
          }
        } catch (error) {
          console.error(`Error removing user ${userIdToRemove} from conversation:`, error);
          // Continue with other users even if one fails
        }
      }
    }
    
    // Add missing users to the conversation
    if (missingUsers.length > 0) {
      for (const missingUserId of missingUsers) {
        try {
          // Try using addGroupMember first (requires admin)
          const addResult = await messagingService.addGroupMember(conversationId, missingUserId, userId);
          if (!addResult.success) {
            // If that fails (user not admin), directly add the participant for task conversations
            // This is safe because we've verified the user has task access
            await updateDoc(conversationRef, {
              participants: arrayUnion(missingUserId),
              updatedAt: serverTimestamp(),
            });
            
            // Create participant document directly
            const participantId = `${conversationId}_${missingUserId}`;
            const participantRef = doc(db, 'conversationParticipants', participantId);
            await setDoc(participantRef, {
              conversationId: conversationId,
              userId: missingUserId,
              organizationId: conversation.organizationId,
              unreadCount: 0,
              joinedAt: serverTimestamp(),
              role: 'member',
            }, { merge: true });
          }
        } catch (error) {
          console.error(`Error adding user ${missingUserId} to conversation:`, error);
          // Continue with other users even if one fails
        }
      }
    }
    
    return { success: true };
  } catch (error: any) {
    console.error('Error syncing task conversation participants:', error);
    return { success: false, error: error.message || 'Failed to sync participants' };
  }
}

/**
 * Get users who have access to view a task based on location and permissions
 */
async function getUsersWithTaskAccess(
  taskLocationId: string,
  organizationId: string,
  taskOwnerRole?: string,
  assignedUserIds?: string[]
): Promise<string[]> {
  try {
    const { collection, query, where, getDocs } = await import('firebase/firestore');
    const { db } = await import('@/firebase/config');
    const { normalizeRoles } = await import('@/constants/roles');
    const { 
      isSystemManager, 
      getUserOwnerRoles,
      getUserSystemRoleForOrganization,
      getUserOwnerRolesForOrganization
    } = await import('@/utils/roleHelpers');
    
    // Get all users in the organization (check both organizationId and organizationIds for backward compatibility)
    const usersRef = collection(db, 'users');
    const usersQuery = query(usersRef, where('organizationId', '==', organizationId));
    const usersSnapshot = await getDocs(usersQuery);
    
    // Also check organizationIds (array-contains)
    const usersQuery2 = query(usersRef, where('organizationIds', 'array-contains', organizationId));
    const usersSnapshot2 = await getDocs(usersQuery2);
    
    const userIds: string[] = [];
    const assignedIdsSet = new Set(assignedUserIds || []);
    const processedUserIds = new Set<string>();
    
    // Helper function to process a user
    const processUser = (userDoc: any) => {
      if (processedUserIds.has(userDoc.id)) return;
      processedUserIds.add(userDoc.id);
      
      const userData = userDoc.data();
      const userLocationIds: string[] = userData.locationIds || [];
      
      // Check if user belongs to this organization (backward compatibility)
      const userOrgId = userData.organizationId;
      const userOrgIds: string[] = userData.organizationIds || [];
      const belongsToOrg = userOrgId === organizationId || userOrgIds.includes(organizationId);
      if (!belongsToOrg) {
        // User doesn't belong to this organization - skip them
        return;
      }
      
      // Check if user is admin or manager (systemRole) - check organization-specific roles first
      const systemRole = getUserSystemRoleForOrganization(userData, organizationId);
      const isAdminOrManager = systemRole === 'admin' || systemRole === 'manager';
      
      // Fallback to legacy role check if organization-specific role not found
      let isAdminOrManagerLegacy = false;
      if (!isAdminOrManager) {
        const userRoles = normalizeRoles(userData?.role || userData?.roles);
        isAdminOrManagerLegacy = isSystemManager(userRoles);
      }
      
      const isAdminOrManagerFinal = isAdminOrManager || isAdminOrManagerLegacy;
      
      if (isAdminOrManagerFinal) {
        // Admins and managers with location access should always be included
        if (userLocationIds.includes(taskLocationId)) {
          userIds.push(userDoc.id);
          console.log(`[getUsersWithTaskAccess] Added admin/manager ${userDoc.id} (${userData.email || userDoc.id}) - has location access to ${taskLocationId}`);
          return;
        } else {
          // Admin/manager doesn't have location access - still include them as they should see all tasks
          userIds.push(userDoc.id);
          console.log(`[getUsersWithTaskAccess] Added admin/manager ${userDoc.id} (${userData.email || userDoc.id}) - no location access, but admin/manager should see all`);
          return;
        }
      }
      
      // For regular users (systemRole 'user'), check location access first
      if (!userLocationIds.includes(taskLocationId)) {
        // User doesn't have access to this location - skip them
        return;
      }
      
      // For regular users, check owner role or assignment
      let userOwnerRoles = getUserOwnerRolesForOrganization(userData, organizationId);
      // Fallback to legacy owner roles if organization-specific roles not found
      if (userOwnerRoles.length === 0) {
        const userRoles = normalizeRoles(userData?.role || userData?.roles);
        const legacyOwnerRoles = getUserOwnerRoles(userRoles);
        if (legacyOwnerRoles.length > 0) {
          userOwnerRoles = [...userOwnerRoles, ...legacyOwnerRoles];
        }
      }
      
      // Include user if:
      // 1. They are assigned to the task, OR
      // 2. They match the owner role
      const isAssigned = assignedIdsSet.has(userDoc.id);
      const matchesOwnerRole = taskOwnerRole && userOwnerRoles.includes(taskOwnerRole);
      
      if (isAssigned || matchesOwnerRole) {
        userIds.push(userDoc.id);
        console.log(`[getUsersWithTaskAccess] Added user ${userDoc.id} (${userData.email || userDoc.id}) - assigned: ${isAssigned}, matchesOwnerRole: ${matchesOwnerRole}`);
      }
    };
    
    // Process users from organizationId query
    usersSnapshot.forEach((userDoc) => {
      processUser(userDoc);
    });
    
    // Process users from organizationIds query (backward compatibility)
    usersSnapshot2.forEach((userDoc) => {
      processUser(userDoc);
    });
    
    console.log(`[getUsersWithTaskAccess] Found ${userIds.length} users with task access for location ${taskLocationId}`);
    return userIds;
  } catch (error: any) {
    console.error('Error getting users with task access:', error);
    return [];
  }
}

/**
 * Create or get a conversation for a specific task
 * Automatically includes all users who have permission to view the task
 */
export async function getOrCreateTaskConversation(
  taskId: string,
  taskLocationId: string,
  taskName: string,
  organizationId: string,
  userId: string,
  taskOwnerRole?: string
): Promise<MessagingIntegrationResult<Conversation>> {
  try {
    // First, check for conversations linked via linkedItems.taskId (more reliable)
    // This should catch conversations created from TaskManager or anywhere else
    const linkedConversationsResult = await getConversationsForTask(taskId);
    if (linkedConversationsResult.success && linkedConversationsResult.data && linkedConversationsResult.data.length > 0) {
      // Sort by updatedAt to get the most recent one
      const sortedConversations = linkedConversationsResult.data.sort((a, b) => {
        const aTime = a.updatedAt?.toDate?.()?.getTime() || a.updatedAt || 0;
        const bTime = b.updatedAt?.toDate?.()?.getTime() || b.updatedAt || 0;
        return bTime - aTime;
      });
          // Verify the conversation still exists and user has access
        const conversation = sortedConversations[0];
        const conversationResult = await messagingService.getConversation(conversation.id!);
        if (conversationResult.success && conversationResult.data) {
          // CRITICAL: Never auto-sync regular groups - they are manually managed
          if (conversationResult.data.type === 'group') {
            console.log('[getOrCreateTaskConversation] Skipping participant sync - regular group conversations are manually managed');
            // Return the conversation without modifying participants
            return { success: true, data: conversationResult.data };
          }
          
          // Get task data to check assignedUserIds
          const { doc: docRef, getDoc: getDocRef } = await import('firebase/firestore');
          const { db: dbRef } = await import('@/firebase/config');
          const taskRefForAssigned = docRef(dbRef, 'tasks', taskId);
          const taskSnapForAssigned = await getDocRef(taskRefForAssigned);
          const assignedUserIds = taskSnapForAssigned.exists() 
            ? (taskSnapForAssigned.data()?.assignedUserIds || (taskSnapForAssigned.data()?.assignedUserId ? [taskSnapForAssigned.data().assignedUserId] : []))
            : [];
          
          // Ensure all users with access are in the conversation
          // Also remove users who no longer have access
          try {
            const allAccessibleUsers = await getUsersWithTaskAccess(taskLocationId, organizationId, taskOwnerRole, assignedUserIds);
            const currentParticipants = conversationResult.data.participants || [];
            
            // Ensure creator is always in allAccessibleUsers (even if getUsersWithTaskAccess didn't find them)
            const allAccessibleUsersWithCreator = [...new Set([userId, ...allAccessibleUsers])];
            
            // Find users who should have access but aren't in the conversation
            const missingUsers = allAccessibleUsersWithCreator.filter(userId => !currentParticipants.includes(userId));
            
            // Find users who are in the conversation but no longer have access (should be removed)
            // BUT: Never remove the creator or admins/managers
            const { doc, updateDoc, arrayUnion, arrayRemove, serverTimestamp, setDoc, deleteDoc } = await import('firebase/firestore');
            const { db } = await import('@/firebase/config');
            const { normalizeRoles } = await import('@/constants/roles');
            const { isSystemManager } = await import('@/utils/roleHelpers');
            const { doc: userDocRef, getDoc: getUserDocRef } = await import('firebase/firestore');
            
            const usersToRemove: string[] = [];
            for (const userIdToCheck of currentParticipants) {
              if (userIdToCheck === userId) {
                // Never remove the creator
                continue;
              }
              if (!allAccessibleUsers.includes(userIdToCheck)) {
                // Check if this user is an admin/manager - if so, don't remove them
                try {
                  const userRefForCheck = userDocRef(db, 'users', userIdToCheck);
                  const userSnapForCheck = await getUserDocRef(userRefForCheck);
                  if (userSnapForCheck.exists()) {
                    const userDataForCheck = userSnapForCheck.data();
                    const userRolesForCheck = normalizeRoles(userDataForCheck?.role || userDataForCheck?.roles);
                    if (isSystemManager(userRolesForCheck)) {
                      // Don't remove admins/managers
                      console.log(`[syncTaskConversation] Not removing admin/manager ${userIdToCheck}`);
                      continue;
                    }
                  }
                } catch (error) {
                  console.error(`Error checking user ${userIdToCheck} role:`, error);
                  // If we can't check, don't remove them to be safe
                  continue;
                }
                usersToRemove.push(userIdToCheck);
              }
            }
            
            const conversationRef = doc(db, 'conversations', conversation.id!);
            
            // Remove users who no longer have access
            // For task conversations, we allow removing users who lost access even if the requester isn't an admin
            if (usersToRemove.length > 0) {
              for (const userIdToRemove of usersToRemove) {
                try {
                  // Try using removeGroupMember first (requires admin)
                  const removeResult = await messagingService.removeGroupMember(conversation.id!, userIdToRemove, userId);
                  if (!removeResult.success) {
                    // If that fails (user not admin), directly remove the participant for task conversations
                    // This is safe because we've verified the user no longer has task access and is not admin/manager
                    await updateDoc(conversationRef, {
                      participants: arrayRemove(userIdToRemove),
                      updatedAt: serverTimestamp(),
                    });
                    
                    // Delete participant document directly
                    const participantId = `${conversation.id!}_${userIdToRemove}`;
                    const participantRef = doc(db, 'conversationParticipants', participantId);
                    await deleteDoc(participantRef);
                  }
                } catch (error) {
                  console.error(`Error removing user ${userIdToRemove} from existing conversation:`, error);
                  // Continue with other users even if one fails
                }
              }
            }
            
            // Add missing users to the conversation
            // For task conversations, we allow adding users with task access even if the requester isn't an admin
            if (missingUsers.length > 0) {
              console.log(`[syncTaskConversation] Adding ${missingUsers.length} missing users to conversation ${conversation.id}`);
              for (const missingUserId of missingUsers) {
                try {
                  // Try using addGroupMember first (requires admin)
                  const addResult = await messagingService.addGroupMember(conversation.id!, missingUserId, userId);
                  if (!addResult.success) {
                    // If that fails (user not admin), directly add the participant for task conversations
                    // This is safe because we've verified the user has task access
                    await updateDoc(conversationRef, {
                      participants: arrayUnion(missingUserId),
                      updatedAt: serverTimestamp(),
                    });
                    
                    // Create participant document directly
                    const participantId = `${conversation.id!}_${missingUserId}`;
                    const participantRef = doc(db, 'conversationParticipants', participantId);
                    await setDoc(participantRef, {
                      conversationId: conversation.id!,
                      userId: missingUserId,
                      organizationId: conversation.organizationId,
                      unreadCount: 0,
                      joinedAt: serverTimestamp(),
                      role: 'member',
                    }, { merge: true });
                    console.log(`[syncTaskConversation] Added user ${missingUserId} to conversation ${conversation.id}`);
                  }
                } catch (error) {
                  console.error(`Error adding user ${missingUserId} to existing conversation:`, error);
                  // Continue with other users even if one fails
                }
              }
            }
        } catch (error) {
          console.error('Error syncing participants for existing conversation:', error);
          // Don't fail the whole operation if this fails
        }
        
        return { success: true, data: conversationResult.data };
      }
    }
    
    // Also check task's messageContext as a fallback
    const { doc, getDoc } = await import('firebase/firestore');
    const { db } = await import('@/firebase/config');
    const taskRef = doc(db, 'tasks', taskId);
    const taskSnap = await getDoc(taskRef);
    
    if (!taskSnap.exists()) {
      return { success: false, error: 'Task not found' };
    }
    
    const taskData = taskSnap.data();
    const existingConversationId = taskData?.messageContext?.conversationId;
    const assignedUserIds = taskData?.assignedUserIds || (taskData?.assignedUserId ? [taskData.assignedUserId] : []);
    
    // Check if conversation exists via task's messageContext
    if (existingConversationId) {
      const conversationResult = await messagingService.getConversation(existingConversationId);
      if (conversationResult.success && conversationResult.data) {
        // Also ensure the conversation is linked via linkedItems (for consistency)
        await linkTaskToConversation(existingConversationId, taskId);
        
        // Ensure all users with access are in the conversation
        // Also remove users who no longer have access
        try {
          const allAccessibleUsers = await getUsersWithTaskAccess(taskLocationId, organizationId, taskOwnerRole, assignedUserIds);
          const conversation = conversationResult.data;
          const currentParticipants = conversation.participants || [];
          
          // Ensure creator is always in allAccessibleUsers (even if getUsersWithTaskAccess didn't find them)
          const allAccessibleUsersWithCreator = [...new Set([userId, ...allAccessibleUsers])];
          
          // Find users who should have access but aren't in the conversation
          const missingUsers = allAccessibleUsersWithCreator.filter(userId => !currentParticipants.includes(userId));
          
          // Find users who are in the conversation but no longer have access (should be removed)
          // BUT: Never remove the creator or admins/managers
          const { doc: docRef2, updateDoc: updateDocRef2, getDoc: getDocRef2, arrayUnion: arrayUnionRef2, arrayRemove: arrayRemoveRef2, serverTimestamp: serverTimestampRef2, setDoc: setDocRef2, deleteDoc: deleteDocRef2 } = await import('firebase/firestore');
          const { db: dbRef2 } = await import('@/firebase/config');
          const { normalizeRoles: normalizeRolesRef2 } = await import('@/constants/roles');
          const { isSystemManager: isSystemManagerRef2 } = await import('@/utils/roleHelpers');
          
          const usersToRemove: string[] = [];
          for (const userIdToCheck of currentParticipants) {
            if (userIdToCheck === userId) {
              // Never remove the creator
              continue;
            }
            if (!allAccessibleUsers.includes(userIdToCheck)) {
              // Check if this user is an admin/manager - if so, don't remove them
              try {
                const userRefForCheck = docRef2(dbRef2, 'users', userIdToCheck);
                const userSnapForCheck = await getDocRef2(userRefForCheck);
                if (userSnapForCheck.exists()) {
                  const userDataForCheck = userSnapForCheck.data();
                  const userRolesForCheck = normalizeRolesRef2(userDataForCheck?.role || userDataForCheck?.roles);
                  if (isSystemManagerRef2(userRolesForCheck)) {
                    // Don't remove admins/managers
                    console.log(`[syncTaskConversation] Not removing admin/manager ${userIdToCheck}`);
                    continue;
                  }
                }
              } catch (error) {
                console.error(`Error checking user ${userIdToCheck} role:`, error);
                // If we can't check, don't remove them to be safe
                continue;
              }
              usersToRemove.push(userIdToCheck);
            }
          }
          
          const conversationRef = docRef2(dbRef2, 'conversations', existingConversationId);
          
          // Remove users who no longer have access
          // For task conversations, we allow removing users who lost access even if the requester isn't an admin
          if (usersToRemove.length > 0) {
            for (const userIdToRemove of usersToRemove) {
              try {
                // Try using removeGroupMember first (requires admin)
                const removeResult = await messagingService.removeGroupMember(existingConversationId, userIdToRemove, userId);
                if (!removeResult.success) {
                  // If that fails (user not admin), directly remove the participant for task conversations
                  // This is safe because we've verified the user no longer has task access
                  await updateDocRef2(conversationRef, {
                    participants: arrayRemoveRef2(userIdToRemove),
                    updatedAt: serverTimestampRef2(),
                  });
                  
                  // Delete participant document directly
                  const participantId = `${existingConversationId}_${userIdToRemove}`;
                  const participantRef = docRef2(dbRef2, 'conversationParticipants', participantId);
                  await deleteDocRef2(participantRef);
                }
              } catch (error) {
                console.error(`Error removing user ${userIdToRemove} from existing conversation:`, error);
                // Continue with other users even if one fails
              }
            }
          }
          
          // Add missing users to the conversation
          // For task conversations, we allow adding users with task access even if the requester isn't an admin
          if (missingUsers.length > 0) {
            console.log(`[syncTaskConversation] Adding ${missingUsers.length} missing users to conversation ${existingConversationId}`);
            for (const missingUserId of missingUsers) {
              try {
                // Try using addGroupMember first (requires admin)
                const addResult = await messagingService.addGroupMember(existingConversationId, missingUserId, userId);
                if (!addResult.success) {
                  // If that fails (user not admin), directly add the participant for task conversations
                  // This is safe because we've verified the user has task access
                  await updateDocRef2(conversationRef, {
                    participants: arrayUnionRef2(missingUserId),
                    updatedAt: serverTimestampRef2(),
                  });
                  
                  // Create participant document directly
                  const participantId = `${existingConversationId}_${missingUserId}`;
                  const participantRef = docRef2(dbRef2, 'conversationParticipants', participantId);
                  await setDocRef2(participantRef, {
                    conversationId: existingConversationId,
                    userId: missingUserId,
                    organizationId: conversation.organizationId,
                    unreadCount: 0,
                    joinedAt: serverTimestampRef2(),
                    role: 'member',
                  }, { merge: true });
                  console.log(`[syncTaskConversation] Added user ${missingUserId} to conversation ${existingConversationId}`);
                }
              } catch (error) {
                console.error(`Error adding user ${missingUserId} to existing conversation:`, error);
                // Continue with other users even if one fails
              }
            }
          }
        } catch (error) {
          console.error('Error syncing participants for existing conversation:', error);
          // Don't fail the whole operation if this fails
        }
        
        return { success: true, data: conversationResult.data };
      }
      // If conversation doesn't exist (deleted), continue to create new one
    }
    
    // Get all users who have access to this task
    const participantIds = await getUsersWithTaskAccess(taskLocationId, organizationId, taskOwnerRole, assignedUserIds);
    
    // Ensure creator is always included (even if getUsersWithTaskAccess didn't find them)
    const participantIdsWithCreator = [...new Set([userId, ...participantIds])];
    
    // Remove current user from participants (they'll be added automatically as creator by createGroupConversation)
    const otherParticipants = participantIdsWithCreator.filter(id => id !== userId);
    
    // Create task conversation with task name
    const conversationName = `${taskName}`;
    const conversationDescription = `Discussion for task: ${taskName}`;
    
    const result = await messagingService.createGroupConversation(
      conversationName,
      conversationDescription,
      otherParticipants,
      organizationId,
      userId,
      'task'
    );

    if (!result.success || !result.data) {
      return { success: false, error: result.error || 'Failed to create conversation' };
    }

    // Link task to conversation and update task with conversation context
    // Do these sequentially to ensure both are saved before returning
    const linkResult = await linkTaskToConversation(result.data.id!, taskId);
    if (!linkResult.success) {
      console.warn('Failed to link task to conversation, but conversation was created:', linkResult.error);
    }
    
    const updateResult = await updateTaskWithMessageContext(taskId, {
      conversationId: result.data.id!,
      lastDiscussedAt: new Date(),
    });
    if (!updateResult.success) {
      console.warn('Failed to update task message context, but conversation was created:', updateResult.error);
    }

    // Send task summary message (only for newly created conversations)
    try {
      await sendTaskSummaryMessage(result.data.id!, taskId, organizationId, userId);
    } catch (error) {
      console.error('Error sending task summary message:', error);
      // Don't fail the whole operation if summary fails
    }

    return { success: true, data: result.data };
  } catch (error: any) {
    console.error('Error getting/creating task conversation:', error);
    await handleFirebaseAuthError(error);
    return { success: false, error: error.message || 'Failed to get/create task conversation' };
  }
}

/**
 * Link a contract to a conversation
 */
export async function linkContractToConversation(
  conversationId: string,
  contractId: string
): Promise<MessagingIntegrationResult<void>> {
  try {
    const conversationRef = doc(db, 'conversations', conversationId);
    const conversationSnap = await getDoc(conversationRef);

    if (!conversationSnap.exists()) {
      return { success: false, error: 'Conversation not found' };
    }

    const conversationData = conversationSnap.data();
    const originalType = conversationData?.type || 'direct';
    
    // Preserve 'group' type if it was originally a group conversation (user-created)
    // Preserve other contextual types ('task', 'checklist', 'form') if already set
    // Only set to 'contract' if it's 'direct' or doesn't have a specific type
    const newType = (originalType === 'group' || originalType === 'task' || originalType === 'checklist' || originalType === 'form') 
      ? originalType 
      : 'contract';

    await updateDoc(conversationRef, {
      'linkedItems.contractId': contractId,
      type: newType,
      updatedAt: serverTimestamp(),
    });

    return { success: true };
  } catch (error: any) {
    console.error('Error linking contract to conversation:', error);
    await handleFirebaseAuthError(error);
    return { success: false, error: error.message || 'Failed to link contract to conversation' };
  }
}

/**
 * Link a contract completion to a conversation
 */
export async function linkContractCompletionToConversation(
  conversationId: string,
  completionId: string
): Promise<MessagingIntegrationResult<void>> {
  try {
    const conversationRef = doc(db, 'conversations', conversationId);
    const conversationSnap = await getDoc(conversationRef);

    if (!conversationSnap.exists()) {
      return { success: false, error: 'Conversation not found' };
    }

    const conversationData = conversationSnap.data();
    const originalType = conversationData?.type || 'direct';
    
    // Preserve 'group' type if it was originally a group conversation (user-created)
    // Preserve other contextual types ('task', 'checklist', 'form') if already set
    // Only set to 'contract' if it's 'direct' or doesn't have a specific type
    const newType = (originalType === 'group' || originalType === 'task' || originalType === 'checklist' || originalType === 'form') 
      ? originalType 
      : 'contract';

    await updateDoc(conversationRef, {
      'linkedItems.contractCompletionId': completionId,
      type: newType,
      updatedAt: serverTimestamp(),
    });

    return { success: true };
  } catch (error: any) {
    console.error('Error linking contract completion to conversation:', error);
    await handleFirebaseAuthError(error);
    return { success: false, error: error.message || 'Failed to link contract completion to conversation' };
  }
}

/**
 * Get or create a conversation for a specific contract completion (signed contract)
 */
export async function getOrCreateContractCompletionConversation(
  completionId: string,
  contractName: string,
  organizationId: string,
  userId: string
): Promise<MessagingIntegrationResult<Conversation>> {
  try {
    // First, check for conversations linked via linkedItems.contractCompletionId
    const { collection, query, where, getDocs } = await import('firebase/firestore');
    const { db } = await import('@/firebase/config');
    
    const conversationsRef = collection(db, 'conversations');
    const conversationsQuery = query(conversationsRef, where('linkedItems.contractCompletionId', '==', completionId));
    const conversationsSnapshot = await getDocs(conversationsQuery);
    
    const linkedConversations: Conversation[] = [];
    conversationsSnapshot.forEach((doc) => {
      const data = doc.data();
      if (data.linkedItems?.contractCompletionId === completionId) {
        linkedConversations.push({ id: doc.id, ...data } as Conversation);
      }
    });
    
    if (linkedConversations.length > 0) {
      // Sort by updatedAt to get the most recent one
      linkedConversations.sort((a, b) => {
        const aTime = a.updatedAt?.toDate?.()?.getTime() || a.updatedAt || 0;
        const bTime = b.updatedAt?.toDate?.()?.getTime() || b.updatedAt || 0;
        return bTime - aTime;
      });
      
      // Verify the conversation still exists
      const conversation = linkedConversations[0];
      const conversationResult = await messagingService.getConversation(conversation.id!);
      if (conversationResult.success && conversationResult.data) {
        return { success: true, data: conversationResult.data };
      }
    }
    
    // Get contract completion to find participants
    const completionRef = doc(db, 'agreementCompletions', completionId);
    const completionSnap = await getDoc(completionRef);
    
    if (!completionSnap.exists()) {
      return { success: false, error: 'Contract completion not found' };
    }
    
    // Create contract conversation with contract name
    const conversationName = `${contractName}`;
    const conversationDescription = `Discussion for contract: ${contractName}`;
    
    // Create with just the current user - participants can be added later
    const result = await messagingService.createGroupConversation(
      conversationName,
      conversationDescription,
      [], // Empty participants for now - can be enhanced later
      organizationId,
      userId,
      'contract'
    );

    if (!result.success || !result.data) {
      return { success: false, error: result.error || 'Failed to create conversation' };
    }

    // Link contract completion to conversation
    await linkContractCompletionToConversation(result.data.id!, completionId);

    return { success: true, data: result.data };
  } catch (error: any) {
    console.error('Error getting/creating contract completion conversation:', error);
    await handleFirebaseAuthError(error);
    return { success: false, error: error.message || 'Failed to get/create contract completion conversation' };
  }
}

/**
 * Get or create a conversation for a specific contract
 */
export async function getOrCreateContractConversation(
  contractId: string,
  contractName: string,
  organizationId: string,
  userId: string
): Promise<MessagingIntegrationResult<Conversation>> {
  try {
    // First, check for conversations linked via linkedItems.contractId
    const { collection, query, where, getDocs } = await import('firebase/firestore');
    const { db } = await import('@/firebase/config');
    
    const conversationsRef = collection(db, 'conversations');
    const conversationsQuery = query(conversationsRef, where('linkedItems.contractId', '==', contractId));
    const conversationsSnapshot = await getDocs(conversationsQuery);
    
    const linkedConversations: Conversation[] = [];
    conversationsSnapshot.forEach((doc) => {
      const data = doc.data();
      if (data.linkedItems?.contractId === contractId) {
        linkedConversations.push({ id: doc.id, ...data } as Conversation);
      }
    });
    
    if (linkedConversations.length > 0) {
      // Sort by updatedAt to get the most recent one
      linkedConversations.sort((a, b) => {
        const aTime = a.updatedAt?.toDate?.()?.getTime() || a.updatedAt || 0;
        const bTime = b.updatedAt?.toDate?.()?.getTime() || b.updatedAt || 0;
        return bTime - aTime;
      });
      
      // Verify the conversation still exists
      const conversation = linkedConversations[0];
      const conversationResult = await messagingService.getConversation(conversation.id!);
      if (conversationResult.success && conversationResult.data) {
        return { success: true, data: conversationResult.data };
      }
    }
    
    // Get contract to find participants (signers)
    const contractRef = doc(db, 'agreementDocuments', contractId);
    const contractSnap = await getDoc(contractRef);
    
    if (!contractSnap.exists()) {
      return { success: false, error: 'Contract not found' };
    }
    
    const contractData = contractSnap.data();
    const signers = contractData?.signers || [];
    
    // Get user IDs for signers (we'll need to look them up by email)
    // For now, we'll create a conversation with the current user and add signers later if needed
    // Or we can get all users in the organization who match the signer emails
    
    // Create contract conversation with contract name
    const conversationName = `${contractName}`;
    const conversationDescription = `Discussion for contract: ${contractName}`;
    
    // For now, create with just the current user - participants can be added later
    const result = await messagingService.createGroupConversation(
      conversationName,
      conversationDescription,
      [], // Empty participants for now - can be enhanced later
      organizationId,
      userId,
      'contract'
    );

    if (!result.success || !result.data) {
      return { success: false, error: result.error || 'Failed to create conversation' };
    }

    // Link contract to conversation
    await linkContractToConversation(result.data.id!, contractId);

    return { success: true, data: result.data };
  } catch (error: any) {
    console.error('Error getting/creating contract conversation:', error);
    await handleFirebaseAuthError(error);
    return { success: false, error: error.message || 'Failed to get/create contract conversation' };
  }
}

/**
 * Send task summary message when conversation is first created
 */
async function sendTaskSummaryMessage(
  conversationId: string,
  taskId: string,
  organizationId: string,
  userId: string
): Promise<void> {
  try {
    // Simple note - TaskCard will display all the details automatically
    const summaryText = "This conversation is linked to this task. You'll receive updates here when the task changes.";

    // Get user info for sender
    const { userService } = await import('@/firebase/database');
    const userResult = await userService.getUser(userId);
    const userName = userResult.success && userResult.data 
      ? (userResult.data.name || userResult.data.email || 'System')
      : 'System';

    // Create message with linked task (TaskCard will display automatically)
    const messagesRef = collection(db, 'messages');
    const messageData = {
      conversationId,
      sentBy: userId,
      sentByName: userName,
      text: summaryText,
      organizationId,
      linkedItems: {
        taskId: taskId,
      },
      messageType: 'task_action',
      createdAt: serverTimestamp(),
    };

    await addDoc(messagesRef, messageData);

    // Update conversation lastMessage
    const conversationRef = doc(db, 'conversations', conversationId);
    await updateDoc(conversationRef, {
      lastMessage: {
        text: summaryText,
        sentBy: userId,
        sentByName: userName,
        sentAt: serverTimestamp(),
      },
      updatedAt: serverTimestamp(),
    });
  } catch (error: any) {
    console.error('Error sending task summary message:', error);
    // Don't throw - this is not critical
  }
}

/**
 * Send vendor summary message when conversation is first created
 */
async function sendVendorSummaryMessage(
  conversationId: string,
  vendorId: string,
  organizationId: string,
  userId: string
): Promise<void> {
  try {
    // Simple note - VendorCard will display all the details automatically
    const summaryText = "This conversation is linked to this vendor. You'll receive updates here when vendor information changes.";

    // Get user info for sender
    const { userService } = await import('@/firebase/database');
    const userResult = await userService.getUser(userId);
    const userName = userResult.success && userResult.data 
      ? (userResult.data.name || userResult.data.email || 'System')
      : 'System';

    // Create message with linked vendor (VendorCard will display automatically)
    const messagesRef = collection(db, 'messages');
    const messageData = {
      conversationId,
      sentBy: userId,
      sentByName: userName,
      text: summaryText,
      organizationId,
      linkedItems: {
        vendorId: vendorId,
      },
      createdAt: serverTimestamp(),
    };

    await addDoc(messagesRef, messageData);

    // Update conversation lastMessage
    const conversationRef = doc(db, 'conversations', conversationId);
    await updateDoc(conversationRef, {
      lastMessage: {
        text: summaryText,
        sentBy: userId,
        sentByName: userName,
        sentAt: serverTimestamp(),
      },
      updatedAt: serverTimestamp(),
    });
  } catch (error: any) {
    console.error('Error sending vendor summary message:', error);
    // Don't throw - this is not critical
  }
}

/**
 * Get or create a conversation for a vendor
 */
export async function getOrCreateVendorConversation(
  vendorId: string,
  vendorName: string,
  organizationId: string,
  userId: string
): Promise<MessagingIntegrationResult<Conversation>> {
  try {
    // First, check for conversations linked via linkedItems.vendorId
    const { collection, query, where, getDocs } = await import('firebase/firestore');
    const { db } = await import('@/firebase/config');
    
    const conversationsRef = collection(db, 'conversations');
    const conversationsQuery = query(conversationsRef, where('linkedItems.vendorId', '==', vendorId));
    const conversationsSnapshot = await getDocs(conversationsQuery);
    
    const linkedConversations: Conversation[] = [];
    conversationsSnapshot.forEach((doc) => {
      const data = doc.data();
      if (data.linkedItems?.vendorId === vendorId) {
        linkedConversations.push({ id: doc.id, ...data } as Conversation);
      }
    });
    
    if (linkedConversations.length > 0) {
      // Sort by updatedAt to get the most recent one
      linkedConversations.sort((a, b) => {
        const aTime = a.updatedAt?.toDate?.()?.getTime() || a.updatedAt || 0;
        const bTime = b.updatedAt?.toDate?.()?.getTime() || b.updatedAt || 0;
        return bTime - aTime;
      });
      
      // Verify the conversation still exists
      const conversation = linkedConversations[0];
      const conversationResult = await messagingService.getConversation(conversation.id!);
      if (conversationResult.success && conversationResult.data) {
        return { success: true, data: conversationResult.data };
      }
    }
    
    // Create group conversation with vendor name
    const conversationName = vendorName;
    const conversationDescription = `Discussion for vendor: ${vendorName}`;
    
    // Create with just the current user - participants can be added later
    const result = await messagingService.createGroupConversation(
      conversationName,
      conversationDescription,
      [], // Empty participants for now - can be enhanced later
      organizationId,
      userId
    );

    if (!result.success || !result.data) {
      return { success: false, error: result.error || 'Failed to create conversation' };
    }

    // Link vendor to conversation
    const conversationRef = doc(db, 'conversations', result.data.id!);
    await updateDoc(conversationRef, {
      'linkedItems.vendorId': vendorId,
      updatedAt: serverTimestamp(),
    });

    // Send vendor summary message (only for newly created conversations)
    try {
      await sendVendorSummaryMessage(result.data.id!, vendorId, organizationId, userId);
    } catch (error) {
      console.error('Error sending vendor summary message:', error);
      // Don't fail the whole operation if summary fails
    }

    return { success: true, data: result.data };
  } catch (error: any) {
    console.error('Error getting/creating vendor conversation:', error);
    await handleFirebaseAuthError(error);
    return { success: false, error: error.message || 'Failed to get/create vendor conversation' };
  }
}

/**
 * Get or create a conversation for an employee
 */
export async function getOrCreateEmployeeConversation(
  employeeId: string,
  employeeName: string,
  organizationId: string,
  userId: string
): Promise<MessagingIntegrationResult<Conversation>> {
  try {
    // First, check for conversations linked via linkedItems.employeeId
    const { collection, query, where, getDocs } = await import('firebase/firestore');
    const { db } = await import('@/firebase/config');
    
    const conversationsRef = collection(db, 'conversations');
    const conversationsQuery = query(conversationsRef, where('linkedItems.employeeId', '==', employeeId));
    const conversationsSnapshot = await getDocs(conversationsQuery);
    
    const linkedConversations: Conversation[] = [];
    conversationsSnapshot.forEach((doc) => {
      const data = doc.data();
      if (data.linkedItems?.employeeId === employeeId) {
        linkedConversations.push({ id: doc.id, ...data } as Conversation);
      }
    });
    
    if (linkedConversations.length > 0) {
      // Sort by updatedAt to get the most recent one
      linkedConversations.sort((a, b) => {
        const aTime = a.updatedAt?.toDate?.()?.getTime() || a.updatedAt || 0;
        const bTime = b.updatedAt?.toDate?.()?.getTime() || b.updatedAt || 0;
        return bTime - aTime;
      });
      
      // Verify the conversation still exists
      const conversation = linkedConversations[0];
      const conversationResult = await messagingService.getConversation(conversation.id!);
      if (conversationResult.success && conversationResult.data) {
        return { success: true, data: conversationResult.data };
      }
    }
    
    // Create group conversation with employee name
    const conversationName = employeeName;
    const conversationDescription = `Discussion for employee: ${employeeName}`;
    
    // Create with just the current user - participants can be added later
    const result = await messagingService.createGroupConversation(
      conversationName,
      conversationDescription,
      [], // Empty participants for now - can be enhanced later
      organizationId,
      userId
    );

    if (!result.success || !result.data) {
      return { success: false, error: result.error || 'Failed to create conversation' };
    }

    // Link employee to conversation
    const conversationRef = doc(db, 'conversations', result.data.id!);
    await updateDoc(conversationRef, {
      'linkedItems.employeeId': employeeId,
      updatedAt: serverTimestamp(),
    });

    return { success: true, data: result.data };
  } catch (error: any) {
    console.error('Error getting/creating employee conversation:', error);
    await handleFirebaseAuthError(error);
    return { success: false, error: error.message || 'Failed to get/create employee conversation' };
  }
}

export const messagingIntegrationService = {
  linkTaskToMessage,
  linkTaskToConversation,
  getConversationsForTask,
  updateTaskWithMessageContext,
  getTaskMessageContext,
  getOrCreateTaskConversation,
  syncTaskConversationParticipants,
  linkContractToConversation,
  getOrCreateContractConversation,
  linkContractCompletionToConversation,
  getOrCreateContractCompletionConversation,
  getOrCreateVendorConversation,
  getOrCreateEmployeeConversation,
};

